import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, PenLine } from "lucide-react";
import { api } from "@/lib/api";

const TEMPLATES = [
  ["standard", "Standard Article"], ["music_news", "Music News"], ["artist_spotlight", "Artist Spotlight"],
  ["album_review", "Album Review"], ["single_review", "Single Review"], ["interview", "Interview"],
  ["top_list", "Top 10 / List"], ["throwback", "Throwback Feature"], ["radio_feature", "Radio Feature"],
];

export default function MagAIWriter() {
  const [params] = useSearchParams();
  const prefArtist = params.get("artist") || "";
  const prefTitle = params.get("title") || "";
  const [prompt, setPrompt] = useState(
    prefArtist ? `Write about "${prefTitle}" by ${prefArtist} — currently playing on our station.` : "");
  const [template, setTemplate] = useState(params.get("type") || "standard");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const nav = useNavigate();

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the article you want"); return; }
    setBusy(true);
    try {
      const r = await api.post("/mag/ai/write", { prompt, template });
      setDraft(r.data);
      toast.success("Draft generated — review it below. Nothing publishes without your approval.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    setBusy(false);
  };

  const openInEditor = async () => {
    setBusy(true);
    try {
      const cats = (await api.get("/mag/categories")).data.categories || [];
      const cat = cats.find((c) => c.name.toLowerCase() === (draft.category || "").toLowerCase());
      const r = await api.post("/mag/articles", {
        title: draft.headline, subtitle: draft.subtitle, body: draft.body_html,
        tags: draft.tags || [], category_ids: cat ? [cat.category_id] : [],
        seo_title: draft.seo_title, meta_description: draft.meta_description,
        social_caption: draft.social_caption, template });
      nav(`/dashboard/magazine/editor/${r.data.article_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create draft"); setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="mag-ai-writer">
      <div>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Sparkles className="h-6 w-6 text-brand-cyan" /> AI Article Writer</h1>
        <p className="text-sm text-ink-secondary mt-1">Describe the story. The AI drafts it — you review, edit and approve. Nothing auto-publishes.</p>
      </div>
      <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3">
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} data-testid="mag-ai-prompt"
          placeholder='e.g. "Create a 700-word feature about the return of modern R&B slow jams."'
          className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm focus:border-brand-cyan focus:outline-none" />
        <div className="flex flex-wrap items-center gap-2">
          <select value={template} onChange={(e) => setTemplate(e.target.value)} data-testid="mag-ai-template"
            className="rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm">
            {TEMPLATES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <button onClick={generate} disabled={busy} data-testid="mag-ai-generate-btn"
            className="inline-flex items-center gap-2 rounded-full bg-brand-cyan/90 text-black px-5 py-2 text-sm font-bold disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate Draft
          </button>
        </div>
      </div>

      {draft && (
        <div className="rounded-xl border border-brand-cyan/40 bg-bg-surface p-5 space-y-3" data-testid="mag-ai-result">
          <div className="text-[10px] font-mono uppercase tracking-wider text-brand-cyan">AI Draft — requires your review</div>
          <h2 className="font-display text-2xl font-bold">{draft.headline}</h2>
          <p className="text-ink-secondary">{draft.subtitle}</p>
          <div className="text-sm leading-relaxed max-h-72 overflow-y-auto border border-line rounded-md p-3 [&_h2]:font-bold [&_h2]:text-lg [&_blockquote]:border-l-4 [&_blockquote]:border-brand-amber [&_blockquote]:pl-3 [&_blockquote]:italic"
            dangerouslySetInnerHTML={{ __html: draft.body_html }} />
          <div className="text-xs font-mono text-ink-muted space-y-1">
            <div>Category: <span className="text-ink-primary">{draft.category}</span> · Tags: {(draft.tags || []).join(", ")}</div>
            <div>SEO: {draft.seo_title}</div>
            <div>Social: {draft.social_caption}</div>
          </div>
          <button onClick={openInEditor} disabled={busy} data-testid="mag-ai-open-editor-btn"
            className="inline-flex items-center gap-2 rounded-full bg-brand-amber text-black px-5 py-2 text-sm font-bold">
            <PenLine className="h-4 w-4" /> Open in Editor
          </button>
        </div>
      )}
    </div>
  );
}
